/**
 * Ajouter un repas à la journée — nom **et** objectifs, dans une feuille.
 *
 * *(04/09/2026, demandé après usage : « pour ajouter un repas, on utilise un
 * bouton ajouter repas qui affiche une fenêtre flottante où l'on met le nom du
 * repas et les objectifs ».)*
 *
 * 🔴 **Remplace la ligne « Nouveau repas » et son dépliement**, essayés la
 * veille. Le champ en pied d'écran suffisait tant qu'un repas n'avait qu'un
 * nom ; dès qu'il en a cinq, une ligne qui pousse quatre cases sous elle fait
 * bouger le pied de page à la frappe — et l'ajout devient un formulaire posé au
 * milieu de la liste des repas. Une feuille dit ce qu'elle est : on compose, on
 * valide, on referme.
 *
 * ⚠️ **Le formulaire vit DANS la feuille, et c'est ce qui le remet à zéro.**
 * `Sheet` rend `null` quand elle est fermée : son contenu est démonté, donc
 * rouvrir « Ajouter un repas » redonne des champs vides sans qu'aucune ligne de
 * code ne les efface. Un `useState` posé au-dessus aurait survécu, et il aurait
 * fallu penser à le vider sur **chacun** des chemins de sortie — validation,
 * bouton « Fermer », glissement vers le bas, voile, touche Échap. Ici l'oubli
 * est impossible.
 *
 * ⚠️ **Aucun aperçu du total de la journée ici** : il se lit sur le bandeau, en
 * haut de l'écran qu'on vient de quitter, et le redire dans la feuille ferait
 * deux comptes de la même grandeur à tenir d'accord.
 */

import { useState } from 'react';

import type { MacroSet } from '../../../../shared/types';
import { Button } from '../../components/nourri';
import { MacroFieldsRow, MacroGapNote } from '../../components/ui/MacroFields';
import { Field, Hint, Sheet } from '../../components/ui/primitives';
import { EMPTY_MACROS, macrosOf, type MacroFields } from '../../lib/formFields';

function NewMealForm({ onAdd }: { onAdd: (name: string, targets: MacroSet) => void }) {
  const [name, setName] = useState('');
  const [targets, setTargets] = useState<MacroFields>(EMPTY_MACROS);

  const trimmed = name.trim();

  return (
    <div className="n-stack">
      {/*
        ⚠️ `autoFocus` est légitime ici — « toujours à la suite d'un geste
        explicite (ouverture d'une feuille), jamais au chargement d'un écran ».
        Et il ne se déclenche qu'à l'ouverture, précisément parce que `Sheet`
        démonte son contenu quand elle se ferme.
      */}
      <Field
        label="Nom du repas"
        value={name}
        onChange={setName}
        placeholder="Collation"
        autoFocus
      />

      <div className="n-stack n-stack--tight">
        <MacroFieldsRow fields={targets} onChange={setTargets} />
        <MacroGapNote macros={macrosOf(targets)} />
        {/*
          ⚠️ **Facultatif, et il faut le dire** : quatre champs présentés sans
          rien se lisent comme obligatoires, et l'on inventerait des chiffres
          pour passer. Laissés vides, le repas n'a pas d'objectif propre — ses
          jauges affichent son total, ce qu'elles ont toujours fait.
        */}
        <Hint>
          Objectifs facultatifs. Laissés vides, le repas n’en a pas — et le total de la journée, qui
          est la somme de ses repas, ne bouge pas.
        </Hint>
      </div>

      <Button
        variant="primary"
        shape="rounded"
        block
        disabled={trimmed === ''}
        onClick={() => {
          onAdd(trimmed, macrosOf(targets));
        }}
      >
        Ajouter le repas
      </Button>
    </div>
  );
}

export function NewMealSheet({
  open,
  onClose,
  onAdd,
}: {
  open: boolean;
  onClose: () => void;
  /** Le nom est déjà élagué et non vide ; les cibles valent zéro si non saisies. */
  onAdd: (name: string, targets: MacroSet) => void;
}) {
  return (
    <Sheet open={open} title="Nouveau repas" onClose={onClose}>
      <NewMealForm
        onAdd={(name, targets) => {
          onAdd(name, targets);
          onClose();
        }}
      />
    </Sheet>
  );
}
